// ABOUTME: Renders one document's change history from the changeLog collection — who changed what, when.
import { ChangeHistoryEntry } from '../../api/adminApi';
import { useDocumentHistory } from '../../hooks/useAdminQueries';
import './ChangeHistoryList.css';

interface ChangeHistoryListProps {
  collection: string;
  documentId: string;
}

const CHANGE_TYPE_LABELS: Record<ChangeHistoryEntry['changeType'], string> = {
  CREATE: 'Created',
  UPDATE: 'Updated',
  DELETE: 'Deleted',
  ACCESS_DENIED: 'Access denied',
};

function formatWhen(createdAt?: string): string {
  if (!createdAt) return 'Unknown time';
  const date = new Date(createdAt);
  return Number.isNaN(date.valueOf()) ? String(createdAt) : date.toLocaleString();
}

function formatWho(source: ChangeHistoryEntry['source']): string {
  if (source.source === 'USER') {
    return source.name || source.email || source.userId || 'Unknown user';
  }
  return source.workflowId ? `System (${source.workflowId})` : 'System';
}

function getValueAtPath(value: unknown, path: string): unknown {
  return path
    .split('.')
    .reduce<unknown>(
      (current, key) =>
        current && typeof current === 'object'
          ? (current as Record<string, unknown>)[key]
          : undefined,
      value,
    );
}

const MAX_VALUE_LENGTH = 120;

/** Compact human-readable rendering of a field value; full detail stays in the raw snapshots. */
function formatValue(value: unknown): { text: string; missing: boolean } {
  if (value === undefined) return { text: 'not set', missing: true };
  if (value === null || value === '') return { text: 'empty', missing: true };
  const text =
    typeof value === 'object' ? JSON.stringify(value) : String(value);
  return {
    text:
      text.length > MAX_VALUE_LENGTH
        ? `${text.slice(0, MAX_VALUE_LENGTH)}…`
        : text,
    missing: false,
  };
}

function FieldValue({
  value,
  side,
}: {
  value: unknown;
  side: 'before' | 'after';
}) {
  const { text, missing } = formatValue(value);
  const missingClass = missing ? ' change-history-value-missing' : '';
  return (
    <span className={`change-history-value change-history-value-${side}${missingClass}`}>
      {text}
    </span>
  );
}

interface ChangeHistoryEntriesProps {
  entries: ChangeHistoryEntry[];
  isLoading: boolean;
  error: unknown;
}

/** Presentation-only history renderer for entries fetched by any query. */
export function ChangeHistoryEntries({
  entries,
  isLoading,
  error,
}: ChangeHistoryEntriesProps) {
  if (isLoading) {
    return <p className="change-history-empty">Loading history…</p>;
  }

  if (error) {
    return (
      <p className="change-history-error">
        Failed to load history: {(error as Error).message}
      </p>
    );
  }

  if (entries.length === 0) {
    return (
      <p className="change-history-empty">
        No recorded changes for this document.
      </p>
    );
  }

  return (
    <ul className="change-history-list">
      {entries.map((entry) => (
        <li key={entry.id} className="change-history-entry">
          <div className="change-history-headline">
            <span
              className={`change-history-type change-history-type-${entry.changeType.toLowerCase()}`}
            >
              {CHANGE_TYPE_LABELS[entry.changeType]}
            </span>
            <span className="change-history-who">
              {formatWho(entry.source)}
              {entry.source.source === 'USER' && entry.source.role && (
                <span className="change-history-role">{entry.source.role}</span>
              )}
            </span>
            <span className="change-history-when">
              {formatWhen(entry.createdAt)}
            </span>
          </div>
          {entry.changedFields.length > 0 && (
            <div className="change-history-diff">
              {entry.changedFields.map((field) => (
                <div key={field} className="change-history-diff-row">
                  <span className="change-history-field">{field}</span>
                  <FieldValue
                    value={getValueAtPath(entry.before, field)}
                    side="before"
                  />
                  <span className="change-history-arrow" aria-hidden="true">
                    →
                  </span>
                  <FieldValue
                    value={getValueAtPath(entry.after, field)}
                    side="after"
                  />
                </div>
              ))}
            </div>
          )}
          <details className="change-history-details">
            <summary>Full snapshots</summary>
            <div className="change-history-snapshots">
              <div>
                <h4>Before</h4>
                <pre>{JSON.stringify(entry.before, null, 2)}</pre>
              </div>
              <div>
                <h4>After</h4>
                <pre>{JSON.stringify(entry.after, null, 2)}</pre>
              </div>
            </div>
          </details>
        </li>
      ))}
    </ul>
  );
}

export function ChangeHistoryList({
  collection,
  documentId,
}: ChangeHistoryListProps) {
  const { data, isLoading, error } = useDocumentHistory(collection, documentId);
  return (
    <ChangeHistoryEntries
      entries={data?.entries ?? []}
      isLoading={isLoading}
      error={error}
    />
  );
}
